import Editor from '@monaco-editor/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EodLatestResponse, PENDING_NAME, IndexComponent, RunResponse, eodApi, executionApi, strategyApi } from '../services/api';
import type { Strategy } from '../services/api';
import StrategyStateBadge from './StrategyStateBadge';

// ---------------------------------------------------------------------------
// Request Edit modal
// ---------------------------------------------------------------------------

function RequestEditModal({
  strategyId,
  onSubmit,
  onCancel,
}: {
  strategyId: number;
  onSubmit: (updated: Strategy) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const MIN = 10;

  const handleSubmit = async () => {
    if (reason.trim().length < MIN) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await strategyApi.requestEdit(strategyId, reason.trim());
      onSubmit(data);
    } catch {
      setError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !submitting && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Code Edit</h3>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Please explain why you need to edit your code
          <span className="text-red-500 ml-1">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          rows={5}
          maxLength={2000}
          placeholder="Describe the changes you need to make and why…"
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent resize-none disabled:opacity-60 transition"
        />
        <p className="text-xs text-gray-400 text-right mt-1">{reason.length} / 2000</p>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || reason.trim().length < MIN}
            className="px-5 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
          >
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EOD date formatter (module-level utility)
// ---------------------------------------------------------------------------

const fmtEodDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// ---------------------------------------------------------------------------
// Available data panel — floating overlay in bottom-left of editor
// ---------------------------------------------------------------------------

type AttrGroup = { group: string; attrs: { name: string; type: string; desc: string }[] };

const SANDBOX_ATTRS: AttrGroup[] = [
  {
    group: 'Equity EOD',
    attrs: [
      { name: 'eod.prices',     type: 'DataFrame', desc: 'date, symbol, open, high, low, close, prev_close, volume' },
      { name: 'eod.universe',   type: 'list[str]', desc: 'sorted list of active NSE symbols' },
      { name: 'eod.as_of',      type: 'str',       desc: 'ISO date of the latest EOD snapshot' },
      { name: 'eod.returns',    type: 'Series',    desc: '% return per symbol (open→close single-day, or multi-day)' },
      { name: 'eod.high_52w',   type: 'Series',    desc: 'max high per symbol across all stored dates' },
      { name: 'eod.low_52w',    type: 'Series',    desc: 'min low per symbol across all stored dates' },
      { name: 'eod.prev_close', type: 'Series',    desc: 'previous close per symbol from Bilav file' },
    ],
  },
  {
    group: 'Classification & Fundamentals',
    attrs: [
      { name: 'eod.sectors',      type: 'DataFrame', desc: 'symbol, sector, industry, mes_sector' },
      { name: 'eod.fundamentals', type: 'DataFrame', desc: 'symbol, isin, sector, market_cap_category, market_cap_cr, pe_ratio, pb_ratio, dividend_yield' },
    ],
  },
  {
    group: 'Other Asset Classes',
    attrs: [
      { name: 'eod.invits', type: 'DataFrame', desc: 'symbol, date, open, high, low, close, volume — InvIT/REIT prices (latest date)' },
      { name: 'eod.bonds',  type: 'DataFrame', desc: 'isin, name, date, open, high, low, close, ytm, rating, maturity_date — bond prices (latest date)' },
    ],
  },
  {
    group: 'Mutual Funds',
    attrs: [
      { name: 'mf.nav',      type: 'DataFrame', desc: 'scheme_code, amfi_code, nav, date — latest NAV per scheme' },
      { name: 'mf.universe', type: 'list[str]', desc: 'sorted list of all distinct amfi_codes with NAV data' },
    ],
  },
];

function DataPanel() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={panelRef} className="absolute bottom-3 left-3 z-10">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border transition ${
          open
            ? 'bg-[#252540] border-gsw-gold/40 text-gsw-gold/80'
            : 'bg-[#252526]/90 border-[#3c3c3c] text-gray-500 hover:text-gray-300 hover:border-gray-500'
        }`}
      >
        <span className="font-mono text-[11px]">{'{ }'}</span>
        <span>Data</span>
      </button>

      {open && (
        <div className="absolute bottom-9 left-0 w-[540px] max-h-[55vh] overflow-y-auto bg-[#1a1a2e] border border-[#3c3c4a] rounded-lg shadow-2xl">
          <div className="sticky top-0 bg-[#1a1a2e] flex items-center justify-between px-4 py-2.5 border-b border-[#3c3c4a]">
            <span className="text-xs font-semibold text-gray-300">Available data</span>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-500 hover:text-gray-300 text-sm leading-none transition"
            >
              ✕
            </button>
          </div>
          <div className="px-4 py-3 space-y-4">
            {SANDBOX_ATTRS.map(group => (
              <div key={group.group}>
                <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-1.5 font-semibold">
                  {group.group}
                </div>
                {group.attrs.map(attr => (
                  <div key={attr.name} className="flex items-baseline gap-2 py-0.5 text-xs">
                    <span className="font-mono text-gsw-gold shrink-0">{attr.name}</span>
                    <span className="text-[#7c7cba] shrink-0">{attr.type}</span>
                    <span className="text-gray-500 truncate">{attr.desc}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slim bottom bar — code visibility toggle
// ---------------------------------------------------------------------------

function VisibilityBottomBar({
  strategyId,
  initial,
  codeViewRequested,
  onUpdate,
}: {
  strategyId: number;
  initial: boolean;
  codeViewRequested: boolean;
  onUpdate: (updated: Strategy) => void;
}) {
  const [allowed, setAllowed] = useState(initial);
  const [requested, setRequested] = useState(codeViewRequested);
  const [pending, setPending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const apply = async (value: boolean) => {
    setPending(true);
    try {
      const { data } = await strategyApi.setCodeVisibility(strategyId, value);
      setAllowed(value);
      setRequested(false);
      onUpdate(data);
    } catch {
      // revert on failure
    } finally {
      setPending(false);
    }
  };

  const handleToggle = () => {
    if (pending) return;
    if (!allowed) setConfirmOpen(true);
    else apply(false);
  };

  if (requested && !allowed) {
    return (
      <div className="shrink-0 bg-amber-950/60 border-t border-amber-500/30 px-5 py-2 flex items-center justify-between gap-4">
        <p className="text-[11px] text-amber-300 leading-none">
          GSW admin has requested to view your strategy code.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => apply(false)}
            disabled={pending}
            className="px-3 py-1 text-[11px] rounded border border-white/20 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-50 transition"
          >
            Deny
          </button>
          <button
            onClick={() => apply(true)}
            disabled={pending}
            className="px-3 py-1 text-[11px] rounded bg-amber-500 text-black font-medium hover:bg-amber-400 disabled:opacity-50 transition"
          >
            {pending ? '…' : 'Allow'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="shrink-0 bg-[#1a1a1a] border-t border-[#2a2a2a] px-5 py-1.5 flex items-center justify-between gap-4">
        <p className="text-[11px] text-gray-600 leading-none">
          Code is private by default — GSW cannot access your methodology without your permission.
        </p>
        <button
          onClick={handleToggle}
          disabled={pending}
          title={allowed ? 'Revoke code visibility' : 'Allow GSW to view your code'}
          className="flex items-center gap-2 text-[11px] disabled:opacity-50 transition shrink-0"
        >
          <span className={`${allowed ? 'text-blue-400' : 'text-gray-500'}`}>
            Allow GSW to view code
          </span>
          <span
            className={`relative w-8 h-4 rounded-full transition-colors shrink-0 ${
              allowed ? 'bg-blue-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${
                allowed ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </span>
        </button>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setConfirmOpen(false)}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Grant code visibility?</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              This will allow the GSW admin team to read your strategy code. You can revoke this
              at any time.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmOpen(false); apply(true); }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Allow Access
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function SpinnerIcon() {
  return (
    <svg className="animate-spin w-3 h-3 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Execution results panels
// ---------------------------------------------------------------------------

function SuccessPanel({ output }: { output: IndexComponent[] }) {
  const total = output.reduce((sum, item) => sum + item.weight, 0);
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const codes = output.map((item) => item.security_code);
    eodApi.resolveNames(codes)
      .then(({ data }) => setNames(data.names))
      .catch(() => {});
  }, [output]);

  return (
    <div className="shrink-0 flex flex-col overflow-hidden border-b border-[#3c3c3c]" style={{ maxHeight: '17rem' }}>
      <div className="shrink-0 px-5 py-2.5 flex items-center gap-2 bg-emerald-950/60 border-b border-emerald-900/40 text-emerald-400 text-sm font-medium">
        <CheckIcon />
        <span>Execution successful</span>
        <span className="ml-auto text-emerald-600 text-xs font-normal">
          {output.length} securities · {total.toFixed(2)}% total weight
        </span>
      </div>
      <div className="overflow-y-auto flex-1 bg-[#161616]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#161616] z-10">
            <tr className="border-b border-[#2e2e2e] text-gray-500 uppercase tracking-wide">
              <th className="px-5 py-2 text-left font-medium">Security Code</th>
              <th className="px-5 py-2 text-left font-medium">Name</th>
              <th className="px-5 py-2 text-right font-medium">Weight %</th>
            </tr>
          </thead>
          <tbody>
            {output.map((item, i) => (
              <tr key={i} className="border-b border-[#1e1e1e] hover:bg-[#202020] transition-colors">
                <td className="px-5 py-1.5 font-mono text-gray-200 tracking-wide whitespace-nowrap">{item.security_code}</td>
                <td className="px-5 py-1.5 text-gray-400 max-w-[260px] truncate">
                  {names[item.security_code] ?? ''}
                </td>
                <td className="px-5 py-1.5 text-right font-mono text-gray-300 whitespace-nowrap">{item.weight.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TIMEOUT_MSG =
  'Your code exceeded the 60 second execution limit. To fix this: reduce the number of securities in your universe, simplify your calculations, or avoid nested loops over large datasets.';

function isTimeoutError(err: string): boolean {
  const lower = err.toLowerCase();
  return lower.includes('timed out') || lower.includes('timeout');
}

function ErrorPanel({ errors }: { errors: string[] }) {
  return (
    <div className="shrink-0 flex flex-col overflow-hidden border-b border-[#3c3c3c]" style={{ maxHeight: '22rem' }}>
      <div className="shrink-0 px-5 py-2.5 flex items-center gap-2 bg-red-950/60 border-b border-red-900/40 text-red-400 text-sm font-medium">
        <XIcon />
        <span>Execution failed — fix your code and resubmit</span>
      </div>
      <div className="overflow-y-auto flex-1 bg-[#161616] px-5 py-3">
        <ul className="space-y-3">
          {errors.map((err, i) =>
            isTimeoutError(err) ? (
              <li key={i} className="flex items-start gap-2 text-xs text-amber-300">
                <span className="shrink-0 mt-px text-amber-500">⚠</span>
                <span className="leading-relaxed">{TIMEOUT_MSG}</span>
              </li>
            ) : (
              <li key={i} className="flex items-start gap-2 text-xs text-red-300">
                <span className="shrink-0 mt-px text-red-500">•</span>
                <pre className="whitespace-pre-wrap font-mono break-words leading-relaxed">{err}</pre>
              </li>
            )
          )}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simulation settings modal
// ---------------------------------------------------------------------------

function SimulationSettingsModal({
  strategy,
  isLocked,
  onSave,
  onClose,
}: {
  strategy: Strategy;
  isLocked: boolean;
  onSave: (updated: Strategy) => void;
  onClose: () => void;
}) {
  const [cadence, setCadence] = useState(strategy.rebalance_cadence ?? 'monthly');
  const [changeMode, setChangeMode] = useState(strategy.change_mode ?? 'significant_only');
  const [threshold, setThreshold] = useState(strategy.significance_threshold ?? 1.0);
  const [fee, setFee] = useState(strategy.expense_ratio_pct_pa ?? 0.0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await strategyApi.update(strategy.id, {
        rebalance_cadence: cadence,
        change_mode: changeMode,
        significance_threshold: threshold,
        expense_ratio_pct_pa: fee,
      });
      onSave(data);
    } catch {
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition';

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Simulation Settings</h3>

        {isLocked && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5">
            Settings locked with submitted code — request an edit to change.
          </p>
        )}

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rebalancing Frequency</label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              disabled={isLocked}
              className={inputClass}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
            <p className="mt-1.5 text-xs text-gray-500">
              Most strategies rebalance monthly or quarterly. Higher frequencies increase transaction costs.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rebalance On</label>
            <div className="space-y-2">
              {([
                ['all_changes', 'All changes'],
                ['significant_only', 'Significant changes only'],
              ] as const).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    name="sim_change_mode"
                    value={value}
                    checked={changeMode === value}
                    onChange={() => setChangeMode(value)}
                    disabled={isLocked}
                    className="accent-gsw-gold"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {changeMode === 'significant_only' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Significance Threshold (%)
              </label>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value))}
                disabled={isLocked}
                min={0.1}
                max={20}
                step={0.1}
                className={inputClass}
              />
              <p className="text-xs text-gray-400 mt-1">
                Minimum weight shift required to trigger a rebalance.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Management Fee (% p.a.)
            </label>
            <input
              type="number"
              value={fee}
              onChange={(e) => setFee(parseFloat(e.target.value))}
              disabled={isLocked}
              min={0}
              max={5}
              step={0.05}
              className={inputClass}
            />
            <p className="text-xs text-gray-400 mt-1">
              Deducted daily from the simulated index (fee ÷ 252).
            </p>
          </div>
        </div>

        <div className="mt-5 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-500 leading-relaxed">
            Platform assumptions: transaction cost 0.20% per trade leg · phased deployment (80% of available cash per day) · T+1 settlement.
          </p>
        </div>

        {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

        <div className="flex gap-3 justify-end mt-6">
          {isLocked ? (
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={saving}
                className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type SaveStatus = 'idle' | 'saving' | 'saved';

const ALWAYS_EDITABLE = new Set<Strategy['state']>(['DRAFT']);
const UNLOCK_EDITABLE = new Set<Strategy['state']>(['LIVE', 'OUTPUT_READY', 'EDIT_REQUESTED']);

interface Props {
  strategy: Strategy;
  onUpdate: (updated: Strategy) => void;
  eodData?: EodLatestResponse | null;
}

export default function StrategyEditor({ strategy, onUpdate, eodData }: Props) {
  const isUnlockEditable = UNLOCK_EDITABLE.has(strategy.state) && strategy.edit_unlocked;
  const isLocked = !ALWAYS_EDITABLE.has(strategy.state) && !isUnlockEditable;
  const canRequestEdit = isLocked && !strategy.edit_unlocked &&
    (['LIVE', 'OUTPUT_READY', 'SUBMITTED'] as Strategy['state'][]).includes(strategy.state);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = [
      '.monaco-editor-overlaymessage { z-index: 9999 !important; top: auto !important; bottom: 0 !important; }',
      '.monaco-editor-overlaymessage .anchor { display: none !important; }',
      '.monaco-editor-overlaymessage .message { z-index: 9999 !important; }',
    ].join(' ');
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const [code, setCode] = useState(strategy.code ?? '');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [showModal, setShowModal] = useState(false);
  const [showRequestEditModal, setShowRequestEditModal] = useState(false);
  const [showSimModal, setShowSimModal] = useState(false);
  const [showSimReminderModal, setShowSimReminderModal] = useState(false);
  const [showEodBar, setShowEodBar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [running, setRunning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [runResult, setRunResult] = useState<RunResponse | null>(() => {
    if (strategy.state === 'OUTPUT_READY' && strategy.index_output?.length) {
      return { success: true, strategy_state: 'OUTPUT_READY', errors: [], index_output: strategy.index_output };
    }
    return null;
  });

  useEffect(() => {
    if (strategy.state === 'SUBMITTED') setRunResult(null);
  }, [strategy.state]);

  const codeRef = useRef(code);
  const isDirtyRef = useRef(false);
  const isSavingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = useCallback(
    async (force = false) => {
      if (!force && !isDirtyRef.current) return;
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      isDirtyRef.current = false;
      setSaveStatus('saving');
      try {
        await strategyApi.update(strategy.id, { code: codeRef.current });
        setSaveStatus('saved');
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
      } catch {
        isDirtyRef.current = true;
        setSaveStatus('idle');
      } finally {
        isSavingRef.current = false;
      }
    },
    [strategy.id],
  );

  useEffect(() => {
    const interval = setInterval(() => save(), 60_000);
    return () => {
      clearInterval(interval);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, [save]);

  const handleCodeChange = (value: string | undefined) => {
    const v = value ?? '';
    codeRef.current = v;
    setCode(v);
    isDirtyRef.current = true;
    setSaveStatus('idle');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(), 3_000);
  };

  const handleManualSave = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    save(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await strategyApi.update(strategy.id, { code: codeRef.current });
      const { data } = await strategyApi.submit(strategy.id);
      onUpdate(data);
      setShowModal(false);
    } catch {
      setSubmitError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const { data } = await strategyApi.reset(strategy.id);
      setRunResult(null);
      onUpdate(data);
    } catch {
      // silent
    } finally {
      setResetting(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const { data } = await executionApi.run(strategy.id);
      setRunResult(data);
      onUpdate({
        ...strategy,
        state: data.strategy_state,
        index_output: data.success ? data.index_output : strategy.index_output,
      });
    } catch {
      setRunResult({
        success: false,
        strategy_state: strategy.state,
        errors: ['Failed to reach the execution server. Please try again.'],
        index_output: null,
      });
    } finally {
      setRunning(false);
    }
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    setSubmitError('');
  };

  // Slim single-line state message shown below the navbar
  const stateMessage = isUnlockEditable
    ? 'GoldStandard Wealth has unlocked your code for editing. Re-submit when done.'
    : isLocked
    ? 'Code submitted and locked — read only.'
    : null;

  return (
    <>
      {/* ── Navbar ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 bg-[#252526] border-b border-[#3c3c3c] px-5 py-2 flex items-center justify-between gap-4">
        {/* Left: name + badge + data tooltip */}
        <div className="flex items-center gap-2.5 min-w-0">
          {strategy.name === PENDING_NAME ? (
            <span className="text-sm italic text-gray-500 truncate">Name pending approval</span>
          ) : (
            <h2 className="text-sm font-semibold text-gray-100 truncate">{strategy.name}</h2>
          )}
          <div className="shrink-0"><StrategyStateBadge state={strategy.state} /></div>
          {eodData && (
            <button
              tabIndex={-1}
              onMouseEnter={() => setShowEodBar(true)}
              onMouseLeave={() => setShowEodBar(false)}
              className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-gray-600 border border-gray-600 hover:border-gray-400 hover:text-gray-300 transition leading-none select-none shrink-0"
            >
              i
            </button>
          )}
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {saveStatus === 'saving' && <span className="text-xs text-gray-500">Saving…</span>}
          {saveStatus === 'saved'  && <span className="text-xs text-emerald-500">Saved ✓</span>}

          <button
            onClick={() => setShowSimModal(true)}
            className="px-3 py-1.5 text-xs border border-[#555] rounded text-gray-300 hover:bg-[#3c3c3c] transition"
          >
            ⚙ Simulation
          </button>

          {!isLocked && (
            <>
              <button
                onClick={handleManualSave}
                disabled={saveStatus === 'saving'}
                className="px-3 py-1.5 text-xs border border-[#555] rounded text-gray-300 hover:bg-[#3c3c3c] disabled:opacity-40 transition"
              >
                Save
              </button>
              <button
                onClick={() => {
                  if (!strategy.simulation_settings_saved_at) {
                    setShowSimReminderModal(true);
                  } else {
                    setShowModal(true);
                  }
                }}
                className="px-3 py-1.5 text-xs bg-gsw-gold text-gsw-navy rounded hover:bg-gsw-gold-light transition font-semibold"
              >
                Submit Final
              </button>
            </>
          )}

          {strategy.state === 'SUBMITTED' && (
            <button
              onClick={handleRun}
              disabled={running}
              className="px-3 py-1.5 text-xs bg-gsw-navy text-gsw-gold rounded hover:bg-gsw-navy/80 disabled:opacity-50 transition font-medium flex items-center gap-1.5 border border-gsw-gold/40"
            >
              {running ? <><SpinnerIcon />Running…</> : 'Run Execution'}
            </button>
          )}

          {canRequestEdit && (
            <button
              onClick={() => setShowRequestEditModal(true)}
              className="px-3 py-1.5 text-xs border border-amber-600/50 rounded text-amber-400 hover:bg-amber-600/10 transition"
            >
              Request Edit
            </button>
          )}

          {process.env.NODE_ENV === 'development' && strategy.state === 'OUTPUT_READY' && (
            <button
              onClick={handleReset}
              disabled={resetting}
              title="Dev only — resets to DRAFT"
              className="px-3 py-1.5 text-xs border border-dashed border-orange-500/60 rounded text-orange-400 hover:bg-orange-500/10 disabled:opacity-40 transition"
            >
              {resetting ? 'Resetting…' : 'Reset'}
            </button>
          )}
        </div>
      </div>

      {/* ── EOD data bar (shown while hovering the info icon) ────────────── */}
      {eodData && showEodBar && (
        <div className="shrink-0 bg-[#1e1e2e] border-b border-[#3c3c4a] px-5 py-2 flex items-center gap-5 text-xs text-gray-400">
          <span>
            <span className="text-gray-500">EOD Prices as of </span>
            {eodData.date ? (
              <>
                <span className="text-white">{fmtEodDate(eodData.date)}</span>
                <span className="text-gray-500"> — </span>
                <span className="text-white">{eodData.security_count.toLocaleString()}</span>
                <span className="text-gray-500"> securities</span>
              </>
            ) : (
              <span className="text-gray-600 italic">not loaded</span>
            )}
          </span>
          <span className="text-gray-700">·</span>
          <span>
            <span className="text-gray-500">Fundamentals: </span>
            {eodData.fundamentals_count > 0 ? (
              <>
                <span className="text-white">{eodData.fundamentals_count.toLocaleString()}</span>
                <span className="text-gray-500"> companies</span>
              </>
            ) : (
              <span className="text-gray-600 italic">not loaded</span>
            )}
          </span>
          <span className="text-gray-700">·</span>
          <span>
            <span className="text-gray-500">Sectors: </span>
            {eodData.sectors_loaded
              ? <span className="text-white">available</span>
              : <span className="text-gray-600 italic">not loaded</span>
            }
          </span>
        </div>
      )}

      {/* ── Slim state message ───────────────────────────────────────────── */}
      {stateMessage && (
        <div className="shrink-0 px-5 py-1.5 bg-[#1c1c1e] border-b border-[#2a2a2a] text-xs text-gsw-gold/80">
          {stateMessage}
        </div>
      )}

      {/* ── Execution results ────────────────────────────────────────────── */}
      {runResult && (
        runResult.success && runResult.index_output
          ? <SuccessPanel output={runResult.index_output} />
          : <ErrorPanel errors={runResult.errors} />
      )}

      {/* ── Editor (fills remaining space) ───────────────────────────────── */}
      <div className="flex-1 overflow-hidden relative">
        <Editor
          height="100%"
          defaultLanguage="python"
          value={code}
          onChange={isLocked ? undefined : handleCodeChange}
          theme="vs-dark"
          options={{
            readOnly: isLocked,
            fontSize: 14,
            fontFamily: "'Fira Code', 'JetBrains Mono', Menlo, monospace",
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            lineNumbers: 'on',
            renderLineHighlight: 'line',
            tabSize: 4,
            insertSpaces: true,
            automaticLayout: true,
            padding: { top: 16, bottom: 16 },
          }}
        />
        <DataPanel />
      </div>

      {/* ── Bottom bar (visibility toggle) ───────────────────────────────── */}
      <VisibilityBottomBar
        strategyId={strategy.id}
        initial={strategy.allow_code_view}
        codeViewRequested={strategy.code_view_requested ?? false}
        onUpdate={onUpdate}
      />

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {showSimModal && (
        <SimulationSettingsModal
          strategy={strategy}
          isLocked={isLocked}
          onSave={(updated) => { setShowSimModal(false); onUpdate(updated); }}
          onClose={() => setShowSimModal(false)}
        />
      )}

      {showSimReminderModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowSimReminderModal(false)}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Simulation Settings Required</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Before submitting, please review and save your simulation settings — rebalancing
              frequency, change sensitivity, and management fee. These define how your strategy's
              index will be calculated.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSimReminderModal(false)}
                className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowSimReminderModal(false); setShowSimModal(true); }}
                className="px-5 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light transition font-semibold"
              >
                Open Simulation Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestEditModal && (
        <RequestEditModal
          strategyId={strategy.id}
          onSubmit={(updated) => { setShowRequestEditModal(false); onUpdate(updated); }}
          onCancel={() => setShowRequestEditModal(false)}
        />
      )}

      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Strategy</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Once submitted, your code will be locked and cannot be edited without GoldStandard
              Wealth authorisation. The output generated from your code will be the property of
              GoldStandard Wealth. Do you confirm?
            </p>
            {submitError && <p className="text-red-500 text-sm mb-4">{submitError}</p>}
            <div className="flex gap-3 justify-end">
              <button
                onClick={closeModal}
                disabled={submitting}
                className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="px-5 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition font-semibold"
              >
                {submitting ? 'Submitting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
